import {useEffect, useCallback, useState} from "react";
import {useNavigate} from "react-router-dom";
import {FaTimes, FaCopy, FaUser, FaComment} from "react-icons/fa";
import {renderIcon, sessionIcon} from "../common/IconLibrary";
import {useToast} from "../common/ToastContext";
import TerminateAssignmentModal from "./TerminateAssignmentModal";
import topicService from "../../services/topicService";
import personaService from "../../services/personaService";
import {currentUserHasRole} from "../../services/userService";

const AssignmentModal = ({
                           assignment,
                           onClose,
                           onAssignmentUpdate,
                           userName,
                         }) => {
  const [canTerminateAssignment, setCanTerminateAssignment] = useState(null);
  const {showToast} = useToast();
  const [showModal, setShowModal] = useState(false);
  const [personaLabel, setPersonaLabel] = useState("");
  const [topicName, setTopicName] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const checkAccess = async () => {
      try {
        const hasAdminAccess = await currentUserHasRole("admin");
        setCanTerminateAssignment(
          hasAdminAccess || userName === assignment.created_by,
        );
      } catch (error) {
        setCanTerminateAssignment(false);
      }
    };
    checkAccess();
  }, []);

  useEffect(() => {
    const loadAssignmentDetails = async () => {
      try {
        const response = await personaService.getPersonaByAssignmentId(assignment.id);
        const persona = response.persona;
        const personaLabel = response.predates_versions ? `${persona.name} (unversioned)` : `${persona.name} (v${persona.version})`;
        setPersonaLabel(personaLabel);

        const topic = await topicService.getTopicByKey(assignment.topic.key);
        setTopicName(topic);
      } catch (error) {
        console.error("Error fetching persona or topic:", error);
      }
    };
    loadAssignmentDetails();
  }, []);

  const handleEscapeKey = useCallback(
    (event) => {
      if (event.key === "Escape") {
        onClose();
        navigate("#");
      }
    },
    [onClose, navigate],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleEscapeKey);
    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [handleEscapeKey]);

  const handleCancelAssignment = () => {
    if (!canTerminateAssignment) {
      showToast(
        "You don't have the necessary permissions to perform this action. If you believe this is an error, please contact your system administrator.",
        "error",
      );
      return;
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    onClose();
    navigate(`#${assignment.id}?reload=true`);
  };

  const handleViewSession = (e) => {
    e.stopPropagation(); // Prevent triggering the row click handler
    navigate(`/sessions?assignment_id=${assignment.id}`);
  };

  const handleCopyAssignmentId = (e) => {
    e.stopPropagation();
    if (navigator.clipboard) {
      navigator.clipboard
        .writeText(assignment.id)
        .then(() => {
          showToast("Assignment ID copied to clipboard", "success");
        })
        .catch((err) => {
          console.error("Failed to copy: ", err);
          showToast("Failed to copy Assignment ID", "error");
        });
    }
  };

  const renderAssignmentProperty = (key, value, level = 0) => {
    if (key === "x-api-key" && typeof value === "string") {
      value = value.substring(0, 4) + "...";
    }

    const isObject =
      value !== null && typeof value === "object" && !Array.isArray(value);
    const isArray = Array.isArray(value);
    return (
      <div key={key} className={`mb-2 ${level > 0 ? "ml-4" : ""}`}>
        <span className="text-blue-400 font-medium">{key}:</span>{" "}
        {isObject ? (
          <div className="mt-1 pl-2 border-l border-gray-600">
            {Object.entries(value).map(([subKey, subValue]) =>
              renderAssignmentProperty(subKey, subValue, level + 1),
            )}
          </div>
        ) : isArray ? (
          <span className="text-green-300">
            {value.length === 0 ? (
              "[]"
            ) : (
              <div className="mt-1 pl-2 border-l border-gray-600">
                {value.map((item, index) => (
                  <div key={index} className="mb-1">
                    {typeof item === "object" && item !== null ? (
                      renderAssignmentProperty(`[${index}]`, item, level + 1)
                    ) : (
                      <span className="text-green-300">{String(item)}</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </span>
        ) : (
          <span className="text-green-300">
            {value === null
              ? "null"
              : value === undefined
                ? "undefined"
                : String(value)}
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center overflow-y-auto z-50">
      <div className="bg-gray-800 p-6 rounded-lg w-full max-w-4xl m-4 shadow-xl overflow-y-auto dark-slim-scrollbar">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-white">
            Assignment ID: {assignment.id}
          </h2>
          <div className="flex items-center">
            <button
              onClick={handleCopyAssignmentId}
              className="flex items-center text-gray-400 hover:text-blue-400 transition-colors mr-4"
              title="Copy ID to clipboard"
            >
              <FaCopy size={14} className="mr-1"/>
              <span className="text-sm">Copy ID</span>
            </button>

            <button
              onClick={handleViewSession}
              className="flex items-center text-gray-400 hover:text-blue-400 transition-colors mr-4"
              title="View related sessions"
            >
              <div className="flex items-center">
                {renderIcon(sessionIcon, 16, 16)}
              </div>
              <span className="text-sm -ml-2">View Sessions</span>
            </button>

            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white"
            >
              <FaTimes size={20}/>
            </button>
          </div>
        </div>
        <div className="mb-4 space-y-2">
          <div className="flex items-center text-gray-300">
            <FaUser className="mr-2"/>
            <span>Persona: {personaLabel || "Loading..."}</span>
          </div>
          <div className="flex items-center text-gray-300">
            <FaComment className="mr-2"/>
            <span>Topic: {topicName?.name || "Loading..."}</span>
          </div>
        </div>
        <div className="bg-gray-700 p-4 max-h-80 rounded-md overflow-y-auto dark-slim-scrollbar">
          {Object.entries(assignment).map(([key, value]) =>
            renderAssignmentProperty(key, value),
          )}
        </div>
        {/* Action buttons */}
        <div className="mt-8 flex justify-between">
          <div>
            <button
              onClick={onClose}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-300 bg-gray-700 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Close
            </button>
          </div>
          <button
            onClick={handleCancelAssignment}
            className={`
              ${canTerminateAssignment ? "inline-flex" : "hidden"}
              items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md ${
              !assignment.state?.open
                ? "bg-gray-500 cursor-not-allowed opacity-60 text-white"
                : "bg-red-600 hover:bg-red-700 text-white"
            } focus:outline-none focus:ring-2 focus:ring-offset-2 ${
              assignment.state?.open ? "focus:ring-red-500" : ""
            }`}
            disabled={!assignment.state?.open}
            title={
              !assignment.state?.open
                ? "Assignment is already closed"
                : "Terminate this assignment"
            }
          >
            Terminate Assignment
          </button>
          {showModal && (
            <TerminateAssignmentModal
              assignment={assignment}
              onClose={() => handleCloseModal()}
              onAssignmentUpdate={onAssignmentUpdate}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AssignmentModal;
